import "./LienInvoicePage.scss";

import FutureMedicalCard from "components/Card/FutureMedicalCard";
import ErrorState from "components/Error/ErrorState";
import KaboodleForm from "components/Forms/KaboodleForm";
import { lienFileToInvoiceableEntity } from "components/Invoicing/invoiceAdapter";
import InvoiceContainer from "components/Invoicing/InvoiceContainer";
import LoadingIcon from "components/Loading/LoadingIcon";
import { getClaimantForm } from "forms/claimant";
import ClientInformationDrawer from "pages/client/ClientInformationDrawer/ClientInformationDrawer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { usePatchClaimantByIdMutation } from "services/claimant-api";
import { useToken } from "utils/context";
import { useSelector } from "utils/redux";

import { useLienFileData } from "./hooks/useLienFileData";
import { useLiensData } from "./hooks/useLiensData";

const LienInvoicePage = () => {
  const { accessToken } = useToken();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const constants = useSelector((state) => state.shared.constants);
  const formMethods = useForm();
  const { clearErrors, setError: setFormError } = formMethods;
  const [patchClaimantById] = usePatchClaimantByIdMutation();

  const { lienFileData } = useLienFileData();

  const {
    liens,
    isLoading: liensLoading,
    error: liensError,
  } = useLiensData({
    fileId: lienFileData?.file_id?.toString(),
  });

  // convert LienFile to invoiceable entity
  const invoiceableEntity = useMemo(() => {
    return lienFileToInvoiceableEntity(lienFileData);
  }, [lienFileData]);

  const handleSave = useCallback(
    async (field: string, value: string | number) => {
      if (!lienFileData?.claimants?.[0]?.id) return;
      if (value === undefined || value === null) return;

      try {
        await patchClaimantById({
          id: lienFileData.claimants[0].id,
          data: { [field]: value },
        });
        clearErrors(field);
      } catch (error) {
        setFormError(field, {
          type: "manual",
          message: error.message || "An error occurred",
        });
      }
    },
    [lienFileData, clearErrors, setFormError, patchClaimantById, accessToken],
  );

  useEffect(() => {
    if (lienFileData?.claimants?.[0]) {
      const claimant = lienFileData.claimants[0];
      formMethods.reset(claimant);
    }
  }, [lienFileData, formMethods]);

  // create the claimant info form content
  const claimantInfoFormContent = useMemo(() => {
    const forms = getClaimantForm(constants);
    return (
      <FormProvider {...formMethods}>
        <KaboodleForm
          classNames="swim-column"
          CardComponent={FutureMedicalCard}
          cards={forms[0].cards || []}
          onSave={handleSave}
        />
      </FormProvider>
    );
  }, [constants, handleSave, formMethods]);

  // handle loading state and errors
  useEffect(() => {
    setIsLoading(liensLoading);
    if (liensError) {
      setError(new Error("An error occurred loading lien data"));
    }
  }, [liensLoading, liensError]);

  if (isLoading) return <LoadingIcon />;
  if (error) return <ErrorState />;

  return (
    <div className="swim-wrapper h-100">
      <div className="grid-layout gl-2 no-gap-tb swimlanes msa-medical-split">
        <div>
          <div className="swim-headers">Claimant Information</div>
          {claimantInfoFormContent}
        </div>
        <div>
          <div className="swim-headers">Invoice</div>
          {invoiceableEntity && (
            <InvoiceContainer
              entity={invoiceableEntity}
              availableLiens={liens?.results || []}
              invoiceType="lien"
            />
          )}
        </div>
      </div>
      <ClientInformationDrawer service={lienFileData} />
    </div>
  );
};

export default LienInvoicePage;
